/**
 * Kassen-Review (Spec 8.1).
 *
 * Die Arbeitsliste der Buchhaltung: alle Sessions mit Flags über alle offenen
 * Tage, blockierende zuerst. Ziel ist ein Halbmonat in wenigen Minuten.
 */
import { Fragment, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api, type CashSessionDto, type WorkstationDto } from "../lib/api";
import { euro, longDate } from "../lib/format";
import { SessionPanel } from "../components/SessionPanel";
import { BulkActions } from "../components/BulkActions";
import {
  Amount,
  Empty,
  ErrorBox,
  FlagChip,
  FlagLegend,
  Info,
  PageHeader,
  Spinner,
  Stat,
  StatusChip,
} from "../components/ui";

type Filter = "blocking" | "flagged" | "all";

export function CashReview() {
  const [filter, setFilter] = useState<Filter>("blocking");
  const [expanded, setExpanded] = useState<string | null>(null);
  // Mehrfachauswahl für Sammelaktionen. Ein Event auf Rechnung betrifft alle
  // Kassen eines Abends; einzeln entschieden wäre das dieselbe Entscheidung
  // zwölfmal.
  const [ausgewaehlt, setAusgewaehlt] = useState<Set<string>>(new Set());
  const queryClient = useQueryClient();

  // Einmal alles laden und im Client filtern: bei rund 200 Sessions je Monat
  // ist das schneller als ein Rundlauf je Filterwechsel, und die Zähler oben
  // beziehen sich dann auf den echten Gesamtbestand.
  const sessions = useQuery({
    queryKey: ["cash-sessions", "all"],
    queryFn: () => api.get<{ sessions: CashSessionDto[] }>("/cash-sessions"),
  });

  const workstations = useQuery({
    queryKey: ["workstations"],
    queryFn: () => api.get<{ workstations: WorkstationDto[] }>("/workstations"),
  });
  // Terminal-ID je Kasse: Damit sucht man im VR-Händler-Report die Umsätze.
  const terminalVon = (id: string) =>
    workstations.data?.workstations.find((w) => w.id === id)?.posTerminalId ?? null;

  const reconcile = useMutation({
    mutationFn: () => api.post("/cash-sessions/reconcile", {}),
    onSuccess: () => queryClient.invalidateQueries(),
  });

  if (sessions.isLoading) return <Spinner label="Kassensessions werden geladen" />;
  if (sessions.error) return <ErrorBox error={sessions.error} />;

  const all = (sessions.data?.sessions ?? []).filter((s) => s.status !== "EXCLUDED");
  const rows =
    filter === "blocking"
      ? all.filter((s) => s.hasBlockingFlag)
      : filter === "flagged"
        ? all.filter((s) => s.flags.length > 0)
        : all;
  const visible = [...rows]
    .sort(
      (a, b) =>
        Number(b.hasBlockingFlag) - Number(a.hasBlockingFlag) ||
        a.businessDate.localeCompare(b.businessDate) ||
        a.workstationName.localeCompare(b.workstationName),
    );

  const auswahl = visible.filter((s) => ausgewaehlt.has(s.id));
  const umschalten = (id: string) =>
    setAusgewaehlt((v) => {
      const neu = new Set(v);
      if (neu.has(id)) neu.delete(id);
      else neu.add(id);
      return neu;
    });
  /** Alle Zeilen eines Betriebstags auf einmal — der übliche Zuschnitt. */
  const tagUmschalten = (datum: string) =>
    setAusgewaehlt((v) => {
      const desTages = visible.filter((s) => s.businessDate === datum).map((s) => s.id);
      const alleDrin = desTages.every((id) => v.has(id));
      const neu = new Set(v);
      for (const id of desTages) {
        if (alleDrin) neu.delete(id);
        else neu.add(id);
      }
      return neu;
    });

  const blocking = all.filter((s) => s.hasBlockingFlag);
  const blockingCount = blocking.length;
  const openDays = new Set(blocking.map((s) => s.businessDate)).size;

  return (
    <>
      <PageHeader
        label="Kassen-Review"
        title="Offene Kassensessions"
        actions={
          <>
            <button
              className="btn-ghost"
              onClick={() => reconcile.mutate()}
              disabled={reconcile.isPending}
            >
              {reconcile.isPending ? "Gleicht ab" : "Neu abgleichen"}
            </button>
          </>
        }
      >
        Jeden Abend melden zwei Geräte dasselbe Geschäft: die Kasse, was sie an Kartenumsatz
        erwartet, und das Karten-Terminal, was tatsächlich angekommen ist. Die Differenz ist
        das Trinkgeld. Hier stehen die Fälle, in denen die beiden nicht zusammenpassen.
      </PageHeader>

      <div className="mb-6 flex flex-wrap gap-6">
        <Stat
          label="blockierend"
          value={String(blockingCount)}
          tone={blockingCount > 0 ? "alert" : "ok"}
          info="Fälle, die eine Entscheidung brauchen. Solange einer offen ist, lässt sich der zugehörige Betriebstag nicht freigeben."
        />
        <Stat
          label="betroffene Tage"
          value={String(openDays)}
          info="Betriebstage, an denen mindestens eine Session offen ist."
        />
        <Stat
          label="Sessions gesamt"
          value={String(all.length)}
          info="Alle geprüften Vorgänge im Bestand. Ein Vorgang ist ein Kassenabschluss mit der zugehörigen Terminal-Auslesung."
        />
      </div>

      <div className="mb-4 flex gap-2">
        {(
          [
            ["blocking", "Blockierend"],
            ["flagged", "Alle markierten"],
            ["all", "Alle"],
          ] as const
        ).map(([value, label]) => (
          <button
            key={value}
            className={filter === value ? "btn-primary" : "btn-ghost"}
            onClick={() => setFilter(value)}
          >
            {label}
          </button>
        ))}
      </div>

      {auswahl.length > 0 ? (
        <BulkActions
          sessions={auswahl}
          onDone={() => setAusgewaehlt(new Set())}
          onCancel={() => setAusgewaehlt(new Set())}
        />
      ) : null}

      <div className="mb-4">
        <FlagLegend />
      </div>

      {reconcile.error ? <ErrorBox error={reconcile.error} /> : null}

      {visible.length === 0 ? (
        <Empty>
          {filter === "blocking"
            ? "Nichts offen. Alle Kassensessions sind geklärt."
            : "Keine Sessions in dieser Auswahl."}
        </Empty>
      ) : (
        <div className="card overflow-hidden">
          <table className="tbl">
            <thead>
              <tr>
                <th className="w-8" />
                <th className="w-32">Betriebstag</th>
                <th className="w-44">Kasse</th>
                <th className="w-28 text-right">
                  Z-Bon
                  <Info text="Was die Kasse an Kartenumsatz gemeldet hat. Steht so auf dem Kassenabschluss aus ecos." />
                </th>
                <th className="w-32 text-right">
                  Kassenschnitt
                  <Info text="Was tatsächlich am Karten-Terminal angekommen ist, laut VR-Export. Die Differenz zum Z-Bon ist das Trinkgeld." />
                </th>
                <th className="w-28 text-right">Trinkgeld</th>
                <th>Hinweise</th>
                <th className="w-24">Status</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((s) => {
                const open = expanded === s.id;
                return (
                  // Der Key gehört an das Fragment, sonst ordnet React die
                  // Zeilen beim Auf- und Zuklappen falsch zu.
                  <Fragment key={s.id}>
                    <tr
                      className={`cursor-pointer ${ausgewaehlt.has(s.id) ? "bg-accent/5" : ""}`}
                      onClick={() => setExpanded(open ? null : s.id)}
                    >
                      <td onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={ausgewaehlt.has(s.id)}
                          onChange={() => umschalten(s.id)}
                          aria-label={`${s.workstationName} am ${s.businessDate} auswählen`}
                        />
                      </td>
                      <td className="whitespace-nowrap">
                        <Link
                          to={`/betriebstage/${s.businessDate}`}
                          className="hover:text-accent"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {longDate(s.businessDate)}
                        </Link>
                        {s.dateChanged ? (
                          <div
                            className="label mt-0.5 normal-case"
                            title={`Vorschlag war ${s.proposedBusinessDate}`}
                          >
                            geändert
                          </div>
                        ) : null}
                        <button
                          className="label mt-0.5 block normal-case hover:text-accent"
                          onClick={(e) => {
                            e.stopPropagation();
                            tagUmschalten(s.businessDate);
                          }}
                          title="Alle Kassen dieses Betriebstags auswählen"
                        >
                          ganzen Tag wählen
                        </button>
                      </td>
                      <td className="whitespace-nowrap">
                        <div>{s.workstationName}</div>
                        {terminalVon(s.workstationId) ? (
                          <div className="text-xs text-muted">
                            Terminal <span className="font-mono">{terminalVon(s.workstationId)}</span>
                          </div>
                        ) : null}
                        {s.actsAsWorkstationName ? (
                          <div className="text-xs text-accent">
                            lief als {s.actsAsWorkstationName}
                          </div>
                        ) : null}
                      </td>
                      <td>
                        <Amount value={euro(s.zBon)} muted={s.zBon === null} />
                      </td>
                      <td>
                        <Amount value={euro(s.cardTotal)} muted={s.cardTotal === null} />
                      </td>
                      <td>
                        <Amount
                          value={euro(s.tipAmount)}
                          className={s.tipAmountOverride ? "underline decoration-dotted" : ""}
                        />
                      </td>
                      <td>
                        <div className="flex flex-wrap gap-1">
                          {s.flags.map((f) => (
                            <FlagChip key={f} flag={f} confirmed={s.status === "CONFIRMED"} />
                          ))}
                        </div>
                      </td>
                      <td>
                        <StatusChip status={s.status} />
                      </td>
                    </tr>
                    {open ? (
                      <tr>
                        <td colSpan={8} className="border-t border-line bg-surface p-0">
                          <SessionPanel
                            session={s}
                            workstations={workstations.data?.workstations ?? []}
                            onDone={() => setExpanded(null)}
                          />
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
